import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Camera, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Age bucket (hours) → warm tone. Fresh = sage, aging = ochre, stale = warm red.
const ageInk = (iso) => {
  if (!iso) return '#8B8377';
  const h = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (h < 36)  return '#2F5D48';
  if (h < 72)  return '#B06A2C';
  return '#8B4513';
};

const relative = (iso) => {
  if (!iso) return 'never';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

/**
 * Compact per-client strip of the last N scan submissions. Lives inside
 * the Daily Recon header so operators can spot a manager who stopped
 * scanning without opening the Links admin tab.
 */
export const ScanSubmissionsStrip = ({ clientId, limit = 7 }) => {
  const [rows, setRows] = useState(null);     // null = not loaded, [] = loaded empty
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!clientId) { setRows(null); return; }
    let cancelled = false;
    axios.get(`${API}/daily-recon/${clientId}/scan-submissions`, { params: { limit } })
      .then((r) => { if (!cancelled) setRows(r.data?.submissions || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Failed to load scan history'); });
    return () => { cancelled = true; };
  }, [clientId, limit]);

  if (!clientId) return null;
  if (err) return null;
  if (rows === null) return null;

  const lastIso = rows[0]?.submitted_at;
  const hoursSince = lastIso ? (Date.now() - new Date(lastIso).getTime()) / 36e5 : Infinity;
  const warnStale = rows.length === 0 || hoursSince > 48;

  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="scan-submissions-strip">
      <div className="flex items-center gap-1.5 text-[10px] tracking-[.14em] uppercase text-[#6E5334]">
        <Camera className="h-3.5 w-3.5" />
        Last {limit} scans
      </div>
      {rows.length === 0 ? (
        <span className="text-[11px] italic text-[#8B4513] flex items-center gap-1" data-testid="scan-history-empty">
          <AlertCircle className="h-3 w-3" />
          No scans submitted yet — issue a scan link from the Links tab.
        </span>
      ) : (
        <>
          <div className="flex items-center gap-1.5">
            {rows.map((s, i) => (
              <div
                key={s.id || i}
                className="h-6 min-w-[26px] px-1.5 rounded-md flex items-center justify-center text-[10px] font-mono text-white"
                style={{ backgroundColor: ageInk(s.submitted_at) }}
                title={`${relative(s.submitted_at)} · ${s.total_rows || 0} rows (${s.auto_imported || 0} auto, ${s.pending || 0} pending) · ${s.image_count || 0} photo${s.image_count === 1 ? '' : 's'}${s.merged_count && s.merged_count !== s.image_count ? ` → ${s.merged_count} merged` : ''}`}
                data-testid={`scan-pill-${i}`}
              >
                {s.total_rows ?? 0}
              </div>
            ))}
          </div>
          <span className="text-[11px] text-[#6E5334]" data-testid="scan-last-seen">
            Last: <b className="text-[#211C16]">{relative(lastIso)}</b>
          </span>
          {warnStale && (
            <span className="text-[11px] text-[#8B4513] flex items-center gap-1" data-testid="scan-stale-warn">
              <AlertCircle className="h-3 w-3" />
              No scan in &gt; 48 h — check in with the manager.
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default ScanSubmissionsStrip;
